import axiosClient from './axiosClient';

/**
 * Where a visitor came from, recorded first-party.
 *
 * Product code should not call this directly — go through `recordTouch` and
 * `identifyTouches` in `lib/touch.ts`, which are the only places that decide
 * what may be sent under which consent.
 */

/** Mirrors the click identifiers in backend/src/models/Attribution.ts. */
export interface ClickIds {
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  fbclid?: string;
  ttclid?: string;
}

export interface TouchInput {
  anonId?: string;
  /**
   * Campaign labels and advertisement identifiers, in any accepted spelling —
   * the server normalises and allow-lists them.
   */
  attribution?: Record<string, string>;
  /** Sent only with advertising consent. See `lib/touch.ts`. */
  clickIds?: ClickIds;
  landingPage?: string;
  referrer?: string;
  courseSlug?: string;
}

/**
 * Records one attributable arrival. Never rejects.
 *
 * A measurement that can throw is a measurement that can break the page it is
 * measuring, and this one runs on every landing. The endpoint answers 204 to
 * everything for the same reason.
 */
export const postMarketingTouch = async (input: TouchInput): Promise<void> => {
  try {
    await axiosClient.post('/attribution/touch', input);
  } catch {
    // Deliberately silent. Nothing on the page depends on this landing.
  }
};

/**
 * Tells the server that the browser it has been recording touches for is this
 * signed-in account.
 *
 * The account's email is read server-side from the session, never sent from
 * here — otherwise any signed-in user could claim another person's marketing
 * history by posting their address.
 */
export const postIdentify = async (anonId?: string): Promise<void> => {
  try {
    await axiosClient.post('/attribution/identify', { anonId });
  } catch {
    // Silent for the same reason. A failed link costs attribution, not access.
  }
};

export interface MyAttributionTouch {
  occurredAt: string;
  touchType: string;
  source: string | null;
  medium: string | null;
  campaign: string | null;
  content: string | null;
  landingPage: string | null;
  leadMagnet: string | null;
  nurtureCampaign: string | null;
}

export interface MyAttribution {
  firstTouch: MyAttributionTouch | null;
  lastTouch: MyAttributionTouch | null;
  touchCount: number;
}

/**
 * How the signed-in account was acquired. Campaign labels only — the response
 * never contains a click identifier, by construction on the server.
 */
export const getMyAttribution = async (): Promise<MyAttribution> => {
  const { data } = await axiosClient.get<MyAttribution>('/attribution/me');
  return data;
};
